import { useDebouncedCallback } from "@mantine/hooks";
import { useEffect } from "react";
import { getConfig } from "../config.ts";
import { useDocumentProgressMutation } from "../queries.ts";

/**
 * Listens for page navigation messages from the Readiant iframe and saves progress.
 * Messages are debounced to prevent excessive API calls during rapid page navigation.
 * @param documentId The ID of the document to track progress for
 */
export function useIframeProgressSync(documentId: string | undefined) {
	const mutation = useDocumentProgressMutation(documentId ?? "");

	const debouncedSaveProgress = useDebouncedCallback((page: number) => {
		if (documentId) {
			mutation.mutate({ progress: page });
		}
	}, 1500);

	useEffect(() => {
		const readiantIframeUrl = getConfig("readiantIframeUrl");
		const expectedOrigin = new URL(readiantIframeUrl).origin;

		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== expectedOrigin) {
				return;
			}

			const data = event.data;
			if (typeof data !== "object" || data === null) {
				return;
			}

			const { type, pages } = data;

			if (
				(type === "next_page" ||
					type === "previous_page" ||
					type === "goto_page") &&
				Array.isArray(pages) &&
				pages.length > 0
			) {
				const currentPage = Math.min(...pages);
				if (Number.isFinite(currentPage)) {
					debouncedSaveProgress(currentPage);
				}
			}
		};

		window.addEventListener("message", handleMessage);

		return () => {
			window.removeEventListener("message", handleMessage);
		};
	}, [debouncedSaveProgress]);
}
